import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { OrgInfo, ProviderResolved } from "shell/api/types";

import {
  lookupEmailOrgs,
  parseIdpConfigs,
} from "common-services/utils/idpDiscoveryUtils";
import { AuthProvider } from "generated/api/organisations/models";

type ProviderDiscoveryState =
  | { step: "email_input" }
  | { step: "loading" }
  | { step: "resolved"; email: string; resolution: ProviderResolved }
  | { step: "error"; message: string };

// This is a temporary measure until we are done with the migration from Cloudentity.
// Once all organisations have been migrated, this list should be removed.
const PASSWORD_LOGIN_DISABLED_ORG_IDS: ReadonlySet<string> = new Set([
  "f6c4acc0-6b97-4e2b-835c-b0c6ed32e8d7", // Zilch dev (user pool: org-zilch)
  "32981ef1-2ccc-4400-8220-998d2bb4c8b0", // Zilch prod (user pool: org-zilch)
]);

// True when at least one org has no active SSO IdP, so its users still need
// the password sign-in option.
const hasOrgWithoutIdp = (orgs: OrgInfo[]): boolean =>
  orgs.some(o => parseIdpConfigs(o.identity_provider_config).length === 0);

const resolveProvider = (orgs: OrgInfo[]): ProviderResolved => {
  // No organisation matched the email domain — send the user straight to the
  // CloudEntity login flow.
  if (orgs.length === 0) {
    return {
      provider: "cloudentity",
      idps: [],
      showPasswordOption: false,
    };
  }

  // Uses the first org with an explicit provider. If a domain has conflicting
  // COGNITO/CLOUDENTITY orgs that is a misconfiguration — ops should fix it.
  const allowedProviders = new Set<AuthProvider>([
    AuthProvider.cognito,
    AuthProvider.cloudentity,
  ]);

  const firstExplicitAuthProviderOrg = orgs.find(
    o => o.auth_provider && allowedProviders.has(o.auth_provider),
  );

  const idps = parseIdpConfigs(orgs.flatMap(o => o.identity_provider_config));
  const passwordLoginAllowed = !orgs.some(
    o =>
      o.organisation_id &&
      PASSWORD_LOGIN_DISABLED_ORG_IDS.has(o.organisation_id),
  );

  switch (firstExplicitAuthProviderOrg?.auth_provider) {
    case AuthProvider.cognito:
      return {
        provider: "cognito",
        idps,
        showPasswordOption: passwordLoginAllowed,
      };

    case AuthProvider.cloudentity:
      return {
        provider: "cloudentity",
        idps: [],
        showPasswordOption: false,
      };

    default: {
      return {
        provider: "unspecified",
        idps,
        showPasswordOption: hasOrgWithoutIdp(orgs) && passwordLoginAllowed,
      };
    }
  }
};

export const useProviderDiscovery = () => {
  const [state, setState] = useState<ProviderDiscoveryState>({
    step: "email_input",
  });
  const { t } = useTranslation(["common"]);

  const lookupDomain = async (email: string) => {
    setState({ step: "loading" });
    const result = await lookupEmailOrgs(email);

    const nextState = result.ok
      ? ({
          step: "resolved",
          email,
          resolution: resolveProvider(result.orgs),
        } as ProviderDiscoveryState)
      : ({
          step: "error",
          message: t(`common:idpDiscovery.${result.error}`),
        } as ProviderDiscoveryState);

    setState(nextState);
  };

  const reset = () => {
    setState({ step: "email_input" });
  };

  return { state, lookupDomain, reset };
};
